import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BookMarked,
  User,
  BookOpen,
  Link,
  ChevronDown,
  ChevronUp,
  Shield,
  Brain,
  Mic,
  MicOff,
  Sparkles,
  Users
} from 'lucide-react';
import ContributionStats from './ContributionStats';
import CIStats from './CIStats';
import { useSearch } from '../../contexts/SearchContext';
import { useCISearch } from '../../contexts/CISearchContext';
import { useJournalSearch } from '../../contexts/JournalSearchContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  useSpeechRecognitionContext,
  PERMISSION_STATES,
  ON_DEVICE_STATES,
  DICTATION_REFUSED_MESSAGE
} from '../../contexts/SpeechRecognitionContext';
import wsService from '../../services/wsService';
import { apiService } from '../../services/api';
import './Sidebar.css';
import UnidentifiedSourcesModal from '../UnidentifiedSourcesModal/UnidentifiedSourcesModal';

// =========================================
// Navigation Items Configuration
// =========================================
// PHIL FEATURE: Admin-only until GA — matches the AdminRoute guard on the
// /phil route and the requireAdminAccess gate on the backend. Hidden from all
// non-admins (a subscriber must not see a nav item the route would block).
// =========================================
const navigationItems = [
  {
    id: 'phil',
    title: 'Phil',
    icon: <Sparkles size={24} strokeWidth={1.5} />,
    path: '/phil',
    expandable: true,
    adminOnly: true
  },
  {
    id: 'ai-explorer',
    title: 'AI Explorer',
    icon: <Brain size={24} strokeWidth={1.5} />,
    path: '/ai-explorer',
    expandable: true
  },
  {
    id: 'knowledge-entries',
    title: 'My Knowledge Entries',
    icon: <BookMarked size={24} strokeWidth={1.5} />,
    path: '/my-knowledge-entries',
    expandable: true
  },
  {
    id: 'collective-intelligence',
    title: 'Collective Intelligence',
    icon: <Users size={24} strokeWidth={1.5} />,
    path: '/collective-intelligence',
    expandable: true
  },
  {
    id: 'journal',
    title: 'My Journal',
    icon: <BookOpen size={24} strokeWidth={1.5} />,
    path: '/journal',
    hidden: false
  },
  {
    id: 'unexplored-links',
    title: 'Unexplored Links',
    icon: <Link size={24} strokeWidth={1.5} />,
    path: '/unexplored-links'
  },
  {
    id: 'account',
    title: 'My Account',
    icon: <User size={24} strokeWidth={1.5} />,
    path: '/account'
  },
  {
    id: 'admin',
    title: 'Admin',
    icon: <Shield size={24} strokeWidth={1.5} />,
    path: '/admin',
    adminOnly: true
  }
];

// =========================================
// Main Sidebar Component
// =========================================
const Sidebar = ({ 
  user, 
  onSearchResults, 
  tagsList,
  selectedTags,
  selectedSources, 
  onSourceSelection, 
  onSourceCorrection
}) => {
  const navigate = useNavigate();
  const location = useLocation();
    
  // =========================================
  // State Management
  // =========================================
  // The Journal search accordion opens by default whenever the Journal view is
  // active; all other sections start collapsed. Lazy init avoids a null→journal
  // flash on a direct load/refresh of /journal.
  const [expandedSection, setExpandedSection] = useState(
    location.pathname === '/journal' ? 'journal' : null
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Phil cluster states
  const [philClusters, setPhilClusters] = useState([]);
  const [philClustersTotal, setPhilClustersTotal] = useState(0);
  const [isLoadingPhilClusters, setIsLoadingPhilClusters] = useState(false);
  const [showAllPhilClusters, setShowAllPhilClusters] = useState(false);

  // Source filtering states
  const [sources, setSources] = useState([]);
  const [visibleSourceCount, setVisibleSourceCount] = useState(10);
  const [isLoadingSources, setIsLoadingSources] = useState(false);

  // Unidentified sources state
  const [unidentifiedDomains, setUnidentifiedDomains] = useState([]);

  // Auth-time pre-loaded sidebar counts. Used to seed the local count state
  // on first render so the sidebar shows real numbers immediately, instead of
  // zeros until an accordion is expanded.
  const { sidebarCounts: authSidebarCounts } = useAuth();

  // Contribution count state — seeded from auth response if already available
  // at mount, otherwise from defaults and synced once auth completes.
  const [contributionCounts, setContributionCounts] = useState(
    authSidebarCounts?.mke ?? {
      titles: 0,
      focuses: 0,
      thoughts: 0
    }
  );

  // Journal: sidebar-local state for the My Journal search accordion.
  const [journalQuery, setJournalQuery] = useState('');

  // CI: sidebar-local state for the Collective Intelligence accordion.
  const [ciQuery, setCiQuery] = useState('');
  const [ciCounts, setCiCounts] = useState(
    authSidebarCounts?.ci ?? {
      documents: 0,
      focuses: 0
    }
  );

  // Seed local count state once auth-time counts arrive (handles the common
  // case where Sidebar mounts before auth initialization completes). After
  // this one-time seed, accordion-expand fetches refresh the values normally.
  const sidebarSeededRef = useRef(!!authSidebarCounts);
  useEffect(() => {
    if (sidebarSeededRef.current) return;
    if (!authSidebarCounts) return;
    if (authSidebarCounts.mke) setContributionCounts(authSidebarCounts.mke);
    if (authSidebarCounts.ci) setCiCounts(authSidebarCounts.ci);
    sidebarSeededRef.current = true;
  }, [authSidebarCounts]);

  // Get search context functions
  const { executeSearch, clearSearch } = useSearch();
  const {
    executeSearch: executeCISearch,
    clearSearch: clearCISearch,
    resultSources,
    selectedSources: ciSelectedSources,
    toggleResultSource
  } = useCISearch();
  const {
    executeSearch: executeJournalSearch,
    clearSearch: clearJournalSearch
  } = useJournalSearch();

  // Get speech recognition context
  const {
    isEnabled: sttEnabled,
    isSupported: sttSupported,
    isRecording,
    isOnDeviceRecognition,
    isDictationRefused,
    onDeviceState,
    permissionState,
    toggleEnabled: toggleSTT,
    requestPermission
  } = useSpeechRecognitionContext();

  // Unidentified Source Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Speech-to-text accordion state
  const [sttAccordionOpen, setSttAccordionOpen] = useState(true);

  // =========================================
  // State Reset Handler
  // =========================================
  const resetState = (section) => {
    if (section !== 'search') {
      setSearchQuery('');
      setVisibleSourceCount(10);
    }
    if (section !== 'ci') {
      setCiQuery('');
    }
    if (section !== 'journal') {
      setJournalQuery('');
    }
  };

  // =========================================
  // Data Loading Functions
  // =========================================
  const loadSources = useCallback(async () => {
    setIsLoadingSources(true);
    try {
      const response = await apiService.source.getSourcesWithCounts();
      const sortedSources = response.sort((a, b) => {
        if (a.domain === 'unidentified') return -1;
        if (b.domain === 'unidentified') return 1;
        return b.count - a.count;
      });
      setSources(sortedSources);

      // Extract unidentified domains from the 'unidentified' entry if present
      const unidentifiedEntry = response.find(s => s.domain === 'unidentified');
      if (unidentifiedEntry?.unidentifiedDomains) {
        setUnidentifiedDomains(unidentifiedEntry.unidentifiedDomains);
      } else {
        setUnidentifiedDomains([]);
      }
    } catch (error) {
      console.error('Error loading sources:', error);
    } finally {
      setIsLoadingSources(false);
    }
  }, []);

  const fetchContributionCounts = useCallback(async () => {
    try {
      const response = await apiService.knowledge.getCounts();
      setContributionCounts(response);
    } catch (error) {
      console.error('Error fetching contribution counts:', error);
    }
  }, []);

  const fetchCICounts = useCallback(async () => {
    try {
      const response = await apiService.ci.getCounts();
      setCiCounts(response);
    } catch (error) {
      console.error('Error fetching CI counts:', error);
    }
  }, []);

  const loadPhilClusters = useCallback(async () => {
    setIsLoadingPhilClusters(true);
    try {
      const response = await apiService.phil.getReadyClusters({ limit: 20 });
      setPhilClusters(response.clusters || []);
      setPhilClustersTotal(response.totalReady || 0);
    } catch (error) {
      console.error('Error loading Phil clusters:', error);
    } finally {
      setIsLoadingPhilClusters(false);
    }
  }, []);

  // =========================================
  // Effect Hooks
  // =========================================
  useEffect(() => {
    if (expandedSection === 'search') {
      loadSources();
      fetchContributionCounts();
    }
    if (expandedSection === 'phil') {
      loadPhilClusters();
    }
    if (expandedSection === 'ci') {
      fetchCICounts();
    }
  }, [expandedSection, loadSources, fetchContributionCounts, loadPhilClusters, fetchCICounts]);

  // Keep the Journal search accordion open by default whenever the Journal
  // view becomes active, and clear the Journal search when navigating away so
  // returning to Journal always shows the full, unfiltered list. Keyed on
  // pathname so it does not re-open after a manual collapse while staying on
  // the page (collapsing does not change the pathname). Journal-only — MKE and
  // other views manage their own search state independently.
  useEffect(() => {
    if (location.pathname === '/journal') {
      setExpandedSection('journal');
    } else {
      setJournalQuery('');
      clearJournalSearch();
    }
  }, [location.pathname, clearJournalSearch]);

  // Refresh MKE sidebar counts when the backend signals a new capture.
  // The WebSocket carries no content — `fetchContributionCounts` hits
  // the existing authenticated HTTP endpoint to get fresh numbers.
  useEffect(() => {
    const handler = () => { fetchContributionCounts(); };
    wsService.subscribe('mke_updated', handler);
    return () => wsService.unsubscribe('mke_updated', handler);
  }, [fetchContributionCounts]);

  // =========================================
  // Event Handlers
  // =========================================
  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      try {
        await executeSearch(searchQuery);
        if (location.pathname !== '/my-knowledge-entries') {
          navigate('/my-knowledge-entries');
        }
      } catch (error) {
        console.error('Search execution error:', error);
      }
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    clearSearch();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch(e);
    }
  };

  // CI handlers — mirror handleSearch / handleClear / handleKeyPress
  const handleCISearch = async (e) => {
    e.preventDefault();
    if (ciQuery.trim()) {
      try {
        await executeCISearch(ciQuery);
        if (location.pathname !== '/collective-intelligence') {
          navigate('/collective-intelligence');
        }
      } catch (error) {
        console.error('CI search execution error:', error);
      }
    }
  };

  const handleCIClear = () => {
    setCiQuery('');
    clearCISearch();
  };

  const handleCIKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCISearch(e);
    }
  };

  // Journal handlers — mirror handleSearch / handleClear / handleKeyPress.
  // executeJournalSearch only sets the active query; Journal.js owns the
  // entry list and reloads it (resetting to page 1) when the query changes.
  const handleJournalSearch = (e) => {
    e.preventDefault();
    if (journalQuery.trim()) {
      executeJournalSearch(journalQuery);
      if (location.pathname !== '/journal') {
        navigate('/journal');
      }
    }
  };

  const handleJournalClear = () => {
    setJournalQuery('');
    clearJournalSearch();
  };

  const handleJournalKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleJournalSearch(e);
    }
  };

  const handleSourceSelect = (sourceId) => {
    onSourceSelection(sourceId);
  };

  const handleUnidentifiedClick = useCallback(() => {
    // Unidentified domains are already loaded from the /counts response
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSourceCorrection = useCallback(async (domain, sourceName) => {
    try {
      await apiService.source.submitCorrection({
        domain,
        suggestedName: sourceName
      });
      await loadSources();
    } catch (error) {
      console.error('Source correction failed:', error);
      throw error;
    }
  }, [loadSources]);

  // =========================================
  // Navigation Handler
  // =========================================
  const handleNavClick = (path, id) => {
    navigate(path);

    if (
      id === 'knowledge-entries' ||
      id === 'admin' ||
      id === 'ai-explorer' ||
      id === 'collective-intelligence' ||
      id === 'journal'
    ) {
      const sectionMap = {
        'knowledge-entries': 'search',
        'admin': 'admin',
        'ai-explorer': 'ai-explorer',
        'phil': 'phil',
        'collective-intelligence': 'ci',
        'journal': 'journal'
      };
      const section = sectionMap[id];

      if (expandedSection === section) {
        setExpandedSection(null);
        resetState(null);
      } else {
        if (expandedSection) {
          resetState(section);
        }
        setExpandedSection(section);
      }
    } else {
      if (expandedSection) {
        resetState(null);
      }
      setExpandedSection(null);
    }
  };

  // =========================================
  // Speech-to-Text Handlers
  // =========================================
  const handleSTTToggle = () => {
    toggleSTT();
  };

  const handleRequestPermission = async () => {
    await requestPermission();
  };

  // =========================================
  // Render Methods
  // =========================================
  const renderCISourceList = () => {
    if (!resultSources || resultSources.length === 0) {
      return null;
    }

    return (
      <div className="source-list">
        {resultSources.map(source => (
          <div key={source.domain} className="source-item">
            <input
              type="checkbox"
              checked={!ciSelectedSources.includes(
                source.domain
              )}
              onChange={() =>
                handleResultSourceToggle(source.domain)
              }
            />
            <span>
              {source.displayName}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const handleResultSourceToggle = (domain) => {
    const willBeExcluded = !ciSelectedSources.includes(
      domain
    );
    toggleResultSource(domain);

    const newExcluded = willBeExcluded
      ? [...ciSelectedSources, domain]
      : ciSelectedSources.filter(d => d !== domain);

    const includedDomains = resultSources
      .map(s => s.domain)
      .filter(d => !newExcluded.includes(d));

    if (ciQuery && ciQuery.trim()) {
      executeCISearch(
        ciQuery,
        1,
        'universal',
        null,
        includedDomains.length < resultSources.length
          ? includedDomains
          : null
      );
    }
  };

  const renderSourceList = () => {
    const regularSources = sources.filter(s => s.domain !== 'unidentified');
    const displaySources = regularSources.slice(0, visibleSourceCount);
    const hasMore = regularSources.length > visibleSourceCount;
    const canCollapse = visibleSourceCount > 10;

    return (
      <div className="source-list">
        {sources.some(s => s.domain === 'unidentified') && (
          <div className="source-item unidentified" onClick={handleUnidentifiedClick}>
            <input
              type="checkbox"
              checked={selectedSources.includes('unidentified')}
              onChange={(e) => { e.stopPropagation(); handleSourceSelect('unidentified'); }}
            />
            <span>Unidentified ({sources.find(s => s.domain === 'unidentified')?.count || 0})</span>
          </div>
        )}

        {displaySources.map(source => (
          <div key={source.domain} className="source-item">
            <input
              type="checkbox"
              checked={selectedSources.includes(source.domain)}
              onChange={() => handleSourceSelect(source.domain)}
            />
            <span>{source.displayName} ({source.count})</span>
          </div>
        ))}

        {(hasMore || canCollapse) && (
          <button
            className="see-more-button"
            onClick={() => {
              if (hasMore) {
                setVisibleSourceCount(prev => prev + 10);
              } else {
                setVisibleSourceCount(10);
              }
            }}
          >
            {hasMore ? (
              <>
                <ChevronDown size={16} />
                See More
              </>
            ) : (
              <>
                <ChevronUp size={16} />
                Show Less
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  const renderSTTAccordion = () => {
    // Don't render if browser doesn't support speech recognition
    if (!sttSupported || isDictationRefused) {
      return (
        <div className="stt-section">
          <div 
            className="stt-header"
            onClick={() => setSttAccordionOpen(!sttAccordionOpen)}
          >
            <div className="stt-header-content">
              <MicOff size={20} strokeWidth={1.5} className="stt-icon" />
              <span className="stt-title">Voice Input</span>
            </div>
            {sttAccordionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {sttAccordionOpen && (
            <div className="stt-content">
              {/* The ruling's wording, verbatim and from one shared constant, so
                  the sidebar and the microphone button cannot drift apart. The
                  old copy named Chrome as the fix, which is now wrong in both
                  directions: Chrome without an on-device language pack is
                  refused too, and the requirement is on-device recognition
                  rather than any particular browser. */}
              <p className="stt-unavailable-message">
                {DICTATION_REFUSED_MESSAGE}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Determine status text
    const getStatusText = () => {
      if (!sttEnabled) return 'Disabled';
      if (isRecording) return 'Recording...';
      if (permissionState === PERMISSION_STATES.DENIED) return 'Permission denied';
      if (onDeviceState === ON_DEVICE_STATES.CHECKING) return 'Checking on-device dictation...';
      if (onDeviceState === ON_DEVICE_STATES.PREPARING) return 'Preparing on-device dictation...';
      if (onDeviceState === ON_DEVICE_STATES.INSTALLABLE) return 'Ready — will finish setting up on first use';
      if (isOnDeviceRecognition) return 'Ready (private, on-device)';
      // Reached only before the probe resolves. Deliberately not 'Ready':
      // nothing is ready until the verdict is in, and dictation is refused
      // until then (ruling point 7 — the on-device label is permitted only
      // when the verdict is confirmed).
      return 'Checking...';
    };

    const getStatusClass = () => {
      if (!sttEnabled) return 'status-disabled';
      if (isRecording) return 'status-recording';
      if (permissionState === PERMISSION_STATES.DENIED) return 'status-error';
      if (!isOnDeviceRecognition) return 'status-disabled';
      return 'status-ready';
    };

    return (
      <div className="stt-section">
        <div 
          className="stt-header"
          onClick={() => setSttAccordionOpen(!sttAccordionOpen)}
        >
          <div className="stt-header-content">
            {sttEnabled ? (
              <Mic size={20} strokeWidth={1.5} className="stt-icon active" />
            ) : (
              <MicOff size={20} strokeWidth={1.5} className="stt-icon" />
            )}
            <span className="stt-title">Voice Input</span>
          </div>
          {sttAccordionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>

        {sttAccordionOpen && (
          <div className="stt-content">
            {/* Enable/Disable Toggle */}
            <div className="stt-toggle-row">
              <label className="stt-toggle-label" htmlFor="stt-toggle">
                Enable voice input
              </label>
              <button
                id="stt-toggle"
                type="button"
                role="switch"
                aria-checked={sttEnabled}
                className={`stt-toggle ${sttEnabled ? 'active' : ''}`}
                onClick={handleSTTToggle}
              >
                <span className="stt-toggle-slider" />
              </button>
            </div>

            {/* Status */}
            <div className={`stt-status ${getStatusClass()}`}>
              {getStatusText()}
            </div>

            {/* Permission prompt if needed */}
            {sttEnabled && permissionState === PERMISSION_STATES.PROMPT && (
              <button 
                className="stt-permission-button"
                onClick={handleRequestPermission}
              >
                Grant microphone access
              </button>
            )}

            {/* Permission denied help */}
            {sttEnabled && permissionState === PERMISSION_STATES.DENIED && (
              <p className="stt-help-text">
                Microphone access was denied. Please enable it in your browser settings to use voice input.
              </p>
            )}

            {/* Privacy note. `isOnDeviceRecognition` is now DERIVED from the
                on-device verdict rather than set independently, so this claim
                renders only when recognition really is confirmed local — and
                recognition cannot start at all unless it is. Before October 12,
                2026 this note could appear while audio went to the browser
                vendor's cloud recognizer. */}
            {sttEnabled && isOnDeviceRecognition && (
              <p className="stt-privacy-note">
                🔒 Your voice is processed on-device and never sent to external servers.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

    const renderPhilAccordion = () => {
      const displayClusters = showAllPhilClusters
        ? philClusters
        : philClusters.slice(0, 10);

      // Format orientation date if available
      const orientationDate = user?.philOrientationCompletedAt
        ? new Date(user.philOrientationCompletedAt).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          })
        : null;

      return (
        <div className="phil-accordion">
          {/* Show orientation info if completed */}
          {orientationDate && (
            <div className="phil-orientation-info">
              <span className="phil-orientation-label">Orientation:</span>
              <button
                className="phil-orientation-link"
                onClick={(e) => {
                  e.stopPropagation();
                  // This will be handled by App.js routing to Phil page with a query param
                  navigate('/phil?showOrientation=true');
                }}
                title="Review Phil orientation"
              >
                {orientationDate}
              </button>
            </div>
          )}

          <div className="phil-accordion-header">
            <span className="phil-accordion-title">Topics Ready for Phil</span>
            <span className="phil-accordion-count">
              {philClustersTotal} ready
            </span>
          </div>
      
          {isLoadingPhilClusters ? (
            <div className="phil-loading">Loading clusters...</div>
          ) : philClusters.length === 0 ? (
            <div className="phil-empty">
              <p>No topics ready yet.</p>
              <p className="phil-empty-hint">
                Keep building your knowledge base with connected thoughts.
              </p>
            </div>
          ) : (
            <>
              <div className="phil-cluster-list">
                {displayClusters.map((cluster, index) => (
                  <div 
                    key={cluster.hubTag || index} 
                    className="phil-cluster-item"
                    onClick={() => navigate('/phil')}
                  >
                    <span className="phil-cluster-name">{cluster.hubTag}</span>
                    <span className="phil-cluster-count">
                      {cluster.thoughtCount || cluster.entryCount}
                    </span>
                  </div>
                ))}
              </div>
          
              {philClustersTotal > 10 && (
                <button
                  className="see-more-button"
                  onClick={() => setShowAllPhilClusters(!showAllPhilClusters)}
                >
                  {showAllPhilClusters ? (
                    <>
                      <ChevronUp size={16} />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown size={16} />
                      See {Math.min(philClustersTotal - 10, 10)} More
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      );
    } ;

  // =========================================
  // Component Render
  // =========================================
  return (
    <div className="sidebar">
      <nav className="sidebar-nav">
        {navigationItems
          .filter(item => !item.hidden)
          .map((item) => {
            // Admin-only items (like Admin panel)
            if (item.adminOnly && !user?.isAdmin) {
              return null;
            }
            // Subscription-gated items (like Phil)
            if (item.requiresSubscription) {
              const hasAccess = ['subscriber', 'champion', 'alpha', 'enterprise'].includes(user?.tier) || user?.isAdmin;
              if (!hasAccess) {
                return null;
              }
            }

            return (
              <React.Fragment key={item.id}>
                <button
                  className={`nav-item ${location.pathname === item.path ? 'active' : ''}
                      ${item.id === 'ai-explorer' && expandedSection === 'ai-explorer' ? 'expanded' : ''}
                      ${item.id === 'knowledge-entries' && expandedSection === 'search' ? 'expanded' : ''}
                      ${item.id === 'collective-intelligence' && expandedSection === 'ci' ? 'expanded' : ''}
                      ${item.id === 'journal' && expandedSection === 'journal' ? 'expanded' : ''}
                      ${item.id === 'admin' && expandedSection === 'admin' ? 'expanded' : ''}`}
                  onClick={() => handleNavClick(item.path, item.id)}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-title">{item.title}</span>
                </button>

                {/* Phil Section */}
                {item.id === 'phil' && expandedSection === 'phil' && (
                  <div className="search-accordion phil-section">
                    {renderPhilAccordion()}
                  </div>
                )}

                {/* AI Explorer Section */}
                {item.id === 'ai-explorer' && expandedSection === 'ai-explorer' && (
                  <div className="search-accordion">
                    <ContributionStats counts={contributionCounts} />
                  </div>
                )}

                {/* Search and Source Filtering for Knowledge Entries */}
                {item.id === 'knowledge-entries' && expandedSection === 'search' && (
                  <div className="search-accordion">
                    <ContributionStats counts={contributionCounts} />

                    <form onSubmit={handleSearch}>
                      <label htmlFor="search">Search</label>
                      <textarea
                        id="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyPress}
                        rows={4}
                        placeholder="Search titles, focuses, thoughts, tags..."
                      />
                      <div className="search-buttons">
                        <button type="submit" className="search-button">
                          Search
                        </button>
                        <button
                          type="button"
                          className="clear-button"
                          onClick={handleClear}
                        >
                          Clear
                        </button>
                      </div>
                      <p className="search-hint">Tip: use "quotes" for exact phrases</p>
                    </form>

                    <div className="source-filter">
                      <h3>Filter by Source</h3>
                      {isLoadingSources ? (
                        <div className="loading-sources">Loading sources...</div>
                      ) : (
                        renderSourceList()
                      )}
                    </div>
                  </div>
                )}

                {/* Collective Intelligence accordion */}
                {item.id === 'collective-intelligence' && expandedSection === 'ci' && (
                  <div className="search-accordion ci-accordion">
                    <CIStats counts={ciCounts} />

                    <form onSubmit={handleCISearch}>
                      <label htmlFor="ci-search">Search</label>
                      <textarea
                        id="ci-search"
                        value={ciQuery}
                        onChange={(e) => setCiQuery(e.target.value)}
                        onKeyDown={handleCIKeyPress}
                        rows={4}
                        placeholder="Search titles, focuses, and tags..."
                      />
                      <div className="search-buttons">
                        <button type="submit" className="search-button">
                          Search
                        </button>
                        <button
                          type="button"
                          className="clear-button"
                          onClick={handleCIClear}
                        >
                          Clear
                        </button>
                      </div>
                      <p className="search-hint">Tip: use "quotes" for exact phrases</p>
                    </form>

                    {resultSources && resultSources.length > 0 && (
                      <div className="source-filter">
                        <h3>Filter by Source</h3>
                        {renderCISourceList()}
                      </div>
                    )}

                    {/* AI EXPLORER — hidden until CI Explorer is designed.
                        Personal KB explorer (above) is the starting point.
                        CI Explorer integration is Phase 3 work.
                        See COLLECTIVE_INTELLIGENCE.md Feature Integration
                        section for the planned interaction model. */}
                    {/*
                    <div className="ci-ai-explorer-placeholder">
                      <label className="ci-placeholder-label">AI Explorer</label>
                      <button
                        type="button"
                        className="search-button"
                        disabled
                        title="Coming soon"
                      >
                        Explore
                      </button>
                    </div>
                    */}
                  </div>
                )}

                {/* My Journal search accordion — reuses the MKE/CI
                    .search-accordion classes for identical styling. */}
                {item.id === 'journal' && expandedSection === 'journal' && (
                  <div className="search-accordion">
                    <form onSubmit={handleJournalSearch}>
                      <label htmlFor="journal-search">Search</label>
                      <textarea
                        id="journal-search"
                        value={journalQuery}
                        onChange={(e) => setJournalQuery(e.target.value)}
                        onKeyDown={handleJournalKeyPress}
                        rows={4}
                        placeholder="Search titles, text, and tags..."
                      />
                      <div className="search-buttons">
                        <button type="submit" className="search-button">
                          Search
                        </button>
                        <button
                          type="button"
                          className="clear-button"
                          onClick={handleJournalClear}
                        >
                          Clear
                        </button>
                      </div>
                      <p className="search-hint">Tip: use "quotes" for exact phrases</p>
                    </form>
                  </div>
                )}

                {/* Admin Section */}
                {item.id === 'admin' && expandedSection === 'admin' && (
                  <div className="search-accordion">
                    <h3>Admin Controls</h3>
                    <div className="admin-controls">
                      <button
                        onClick={() => navigate('/admin/organizations')}
                        className="search-button"
                      >
                        Organizations
                      </button>
                      <button
                        onClick={() => navigate('/admin/costs')}
                        className="search-button mt-2"
                      >
                        AI & Costs
                      </button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </nav>

      {/* Speech-to-Text Section */}
      {renderSTTAccordion()}

      {/* Unidentified Sources Modal */}
      <UnidentifiedSourcesModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        unidentifiedSources={unidentifiedDomains}
        onSourceCorrection={handleSourceCorrection}
      />

    </div>  
  );
};

export default Sidebar;
